import { useEffect, useCallback } from 'react'

import { appContextReducerActions } from 'contexts/app-context/utils'
import { isAppContextPrivacyCenterCacheStale } from 'contexts/app-context/utils/app-context-cache'
import { isEmpty } from 'lodash'
import { IS_DEV_ENV } from 'common/constants'

import { useAppContext } from './useAppContext'
import { getPrivacyCenter } from '../repository/getPrivacyCenter'

const useSetPrivacyCenterContent = () => {
  const {
    dispatch,
    privacyCenterContent,
    appContextCachedAt,
    userAccountType,
    userCountry,
    userLanguage,
    userSelectedEntity,
    isAppError,
    isAppLoading,
  } = useAppContext()

  const hasPrivacyCenterRequestData =
    !!userAccountType && !!userCountry && !!userLanguage && !!userSelectedEntity

  const isPrivacyCenterCacheStale = isAppContextPrivacyCenterCacheStale(appContextCachedAt)

  const setPrivacyCenterInfo = useCallback(async () => {
    if (!hasPrivacyCenterRequestData) return

    try {
      dispatch(appContextReducerActions.setLoading(true))
      dispatch(appContextReducerActions.setError(false))

      const { privacyCenterComponents, privacyCenterEndpoints } = await getPrivacyCenter({
        userAccountType,
        userCountry,
        userSelectedEntity,
        userLanguage,
      })

      if (isEmpty(privacyCenterComponents) || isEmpty(privacyCenterEndpoints)) {
        if (IS_DEV_ENV) {
          global.console.debug('Failed to fetch privacyCenterComponents or privacyCenterEndpoints')
        }

        dispatch(appContextReducerActions.setError(true))
      }

      dispatch(
        appContextReducerActions.setPrivacyCenterContent({
          components: privacyCenterComponents.components,
          isActive: privacyCenterComponents.isActive,
          entity: userSelectedEntity,
          endpoints: privacyCenterEndpoints,
        }),
      )
    } catch (error) {
      if (IS_DEV_ENV) {
        global.console.debug('Failed to initialize app context:', error)
      }

      dispatch(appContextReducerActions.setError(true))
    } finally {
      dispatch(appContextReducerActions.setLoading(false))
    }
  }, [
    dispatch,
    hasPrivacyCenterRequestData,
    userAccountType,
    userCountry,
    userLanguage,
    userSelectedEntity,
  ])

  useEffect(() => {
    if (isAppLoading || isAppError || !hasPrivacyCenterRequestData) return

    const shouldLoadPrivacyCenterContent =
      isEmpty(privacyCenterContent) || isPrivacyCenterCacheStale

    if (shouldLoadPrivacyCenterContent) setPrivacyCenterInfo()
  }, [
    appContextCachedAt,
    hasPrivacyCenterRequestData,
    isAppError,
    isAppLoading,
    isPrivacyCenterCacheStale,
    privacyCenterContent,
    setPrivacyCenterInfo,
  ])
}

export { useSetPrivacyCenterContent }
